import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, X, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';

const WEEKDAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export const TimesheetCalendar = ({ year, month, entries, canEdit, onSave, onDelete, projects = [] }) => {
  const [dialogDate, setDialogDate] = useState(null);
  const [start, setStart] = useState('07:30');
  const [end, setEnd] = useState('16:00');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstOffset = (new Date(year, month - 1, 1).getDay() + 6) % 7; // maandag = 0
  const todayStr = new Date().toISOString().slice(0, 10);
  const dateStr = (d) => `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const openDay = (d) => {
    const ds = dateStr(d);
    if (!canEdit(ds)) {
      toast.error('Deze dag kan je niet (meer) aanpassen — alleen deze week');
      return;
    }
    const e = entries[ds];
    setStart(e?.start_time || '07:30');
    setEnd(e?.end_time || '16:00');
    setRows((e?.projects || []).map(p => ({ project_id: p.project_id, label: p.label, hours: p.hours })));
    setDialogDate(ds);
  };

  const dayHours = (() => {
    try {
      const [sh, sm] = start.split(':').map(Number);
      const [eh, em] = end.split(':').map(Number);
      return Math.max(Math.round(((eh * 60 + em - sh * 60 - sm) / 60 - 1) * 100) / 100, 0);
    } catch { return 0; }
  })();
  const splitTotal = Math.round(rows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0) * 100) / 100;

  const addRow = () => {
    const used = new Set(rows.map(r => r.project_id));
    const next = projects.find(p => !used.has(p.id));
    if (!next && projects.length === 0) {
      toast.error('Geen projecten aan jou toegewezen in deze periode');
      return;
    }
    const remaining = Math.max(dayHours - splitTotal, 0);
    setRows([...rows, { project_id: next?.id || projects[0]?.id || '', label: next?.label || projects[0]?.label || '', hours: remaining || dayHours }]);
  };
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    setSaving(true);
    try {
      const projectRows = rows
        .filter(r => r.project_id && parseFloat(r.hours) > 0)
        .map(r => ({ project_id: r.project_id, label: r.label, hours: parseFloat(r.hours) }));
      await onSave(dialogDate, start, end, projectRows);
      setDialogDate(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await onDelete(dialogDate);
      setDialogDate(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="timesheet-calendar">
      <div className="grid grid-cols-7 gap-1.5 text-center text-xs text-muted-foreground mb-1.5">
        {WEEKDAYS.map(w => <div key={w}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: firstOffset }).map((_, i) => <div key={`e${i}`} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const d = i + 1;
          const ds = dateStr(d);
          const e = entries[ds];
          const editable = canEdit(ds);
          return (
            <button
              key={ds}
              onClick={() => openDay(d)}
              data-testid={`ts-day-${ds}`}
              className={`min-h-[68px] rounded-md border p-1.5 text-left transition-colors flex flex-col
                ${e ? 'bg-orange-500/15 border-orange-500/50' : 'bg-card hover:bg-muted/40'}
                ${ds === todayStr ? 'ring-1 ring-orange-500' : ''}
                ${!editable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className="text-xs font-medium">{d}</span>
              {e && (
                <span className="mt-auto">
                  <span className="block text-[10px] text-muted-foreground leading-tight">{e.start_time}-{e.end_time}</span>
                  <span className="flex items-center gap-1">
                    <span className="text-xs font-bold text-orange-500">{e.hours}u</span>
                    {e.projects?.length > 0 && (
                      <FolderKanban className="w-3 h-3 text-blue-500" data-testid={`ts-day-projects-${ds}`} />
                    )}
                  </span>
                </span>
              )}
            </button>
          );
        })}
      </div>

      <Dialog open={!!dialogDate} onOpenChange={(o) => !o && setDialogDate(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Werkdag {dialogDate && new Date(dialogDate).toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' })}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Startuur</Label>
              <Input type="time" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ts-start-input" />
            </div>
            <div>
              <Label>Einduur</Label>
              <Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ts-end-input" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Per werkdag wordt automatisch 1 uur pauze afgetrokken.</p>

          <div className="space-y-2 border-t pt-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><FolderKanban className="w-4 h-4 text-blue-500" /> Uren per project</Label>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="ts-add-project-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Project
              </Button>
            </div>
            {rows.length === 0 && (
              <p className="text-xs text-muted-foreground">Optioneel: verdeel je uren over de projecten waar je aan werkte.</p>
            )}
            {rows.map((r, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`ts-project-row-${i}`}>
                <select
                  value={r.project_id}
                  onChange={(e) => {
                    const p = projects.find(x => x.id === e.target.value);
                    updateRow(i, { project_id: e.target.value, label: p?.label || r.label });
                  }}
                  className="flex-1 min-w-0 h-9 rounded-md border bg-background px-2 text-sm"
                  data-testid={`ts-project-select-${i}`}
                >
                  {!projects.some(p => p.id === r.project_id) && r.project_id && (
                    <option value={r.project_id}>{r.label}</option>
                  )}
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.label}</option>
                  ))}
                </select>
                <Input
                  type="number" step="0.5" min="0.5" max="24"
                  value={r.hours}
                  onChange={(e) => updateRow(i, { hours: e.target.value })}
                  className="w-20 flex-shrink-0"
                  data-testid={`ts-project-hours-${i}`}
                />
                <Button variant="ghost" size="icon" className="flex-shrink-0 h-8 w-8" onClick={() => removeRow(i)} data-testid={`ts-project-remove-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {rows.length > 0 && (
              <p className={`text-xs ${Math.abs(splitTotal - dayHours) < 0.01 ? 'text-green-600' : 'text-amber-600'}`} data-testid="ts-split-total">
                Verdeeld: {splitTotal}u van {dayHours}u werkdag
              </p>
            )}
          </div>

          <div className="flex justify-between gap-2">
            {dialogDate && entries[dialogDate] ? (
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={saving} data-testid="ts-delete-btn">
                Werkdag verwijderen
              </Button>
            ) : <span />}
            <Button onClick={handleSave} disabled={saving} data-testid="ts-save-btn">
              {saving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
